/* eslint-disable react/display-name */
import {
  Card,
  Heading,
  Flex,
  DataList,
  Text,
  Tooltip,
  Icon,
  Badge,
  SegmentedControl,
  IconButton,
  DataTable,
  Box,
  Skeleton,
} from '@scale/scaleui-radix';
import { faDownload, faInfoCircle, faRefresh } from '@fortawesome/pro-regular-svg-icons';

import {
  GetCollectionSession,
  CollectionSessionWithHydratedUsers,
  HydratedUser,
} from '@scale/shared/webinarSessions/types';
import { useState, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import {
  isPastSession,
  formatDurationSeconds,
} from 'react-scale/components/WebinarSessions/helpers';
import AddAttendees from 'react-scale/components/WebinarSessions/components/AddAttendees';
import moment from 'moment';
import { useMutation, useQueryClient } from '@tanstack/react-query-v4';
import { collectRegistrants } from 'react-scale/components/WebinarSessions/api';
import { toast } from 'react-toastify';

enum FilterCategory {
  All = 'all',
  Invited = 'invited',
  Registered = 'registered',
  Attended = 'attended',
  NoResponse = 'no_response',
}

type Filters = {
  category: FilterCategory;
  search: string;
};

interface MetricCardProps {
  label: string;
  value: number;
  tooltip: string;
  onSync?: () => void;
  isSyncing?: boolean;
}

const MetricCard = ({ label, value, tooltip, onSync, isSyncing }: MetricCardProps) => {
  const syncButton = onSync ? (
    <IconButton variant="ghost" size="1" color="neutral" onClick={onSync} disabled={isSyncing}>
      <Tooltip content="Collect Zoom registrants">
        <Icon icon={faRefresh} size="1" label="refresh" />
      </Tooltip>
    </IconButton>
  ) : null;

  return (
    <Card variant="surface" style={{ flex: 1 }}>
      <DataList.Root orientation="vertical">
        <DataList.Item>
          <DataList.Label>
            <Flex align="center" gap="1">
              {label}
              <Tooltip content={tooltip}>
                <Icon icon={faInfoCircle} size="1" label={tooltip} />
              </Tooltip>
              {syncButton}
            </Flex>
          </DataList.Label>
          <DataList.Value>
            <Text size="6">{value}</Text>
          </DataList.Value>
        </DataList.Item>
      </DataList.Root>
    </Card>
  );
};

interface MetricCardListProps {
  allUsers: AggregateUsers;
  onSync?: () => void;
  isSyncing?: boolean;
}

const MetricCardList = ({ allUsers, onSync, isSyncing }: MetricCardListProps) => {
  const numInvited = allUsers.userStats.invitedUsersTruncated
    ? allUsers.userStats.invitedUsersCount
    : allUsers.aggregateUsers.filter(user => user.invited).length;
  const registered = allUsers.aggregateUsers.filter(user => user.registered);
  const attended = allUsers.aggregateUsers.filter(user => user.attended);

  return (
    <Flex gap="4">
      <MetricCard label="Invited" value={numInvited} tooltip="Users invited to the webinar" />
      <MetricCard
        label="Registered"
        value={registered.length}
        tooltip="Users that RSVP'd yes to the webinar"
        onSync={onSync}
        isSyncing={isSyncing}
      />
      {attended.length > 0 && (
        <MetricCard
          label="Attended"
          value={attended.length}
          tooltip="Users who attended the webinar"
        />
      )}
    </Flex>
  );
};

type AggregateUser = {
  id: string;
  email: string;
  invited: boolean;
  registered: boolean;
  attended: boolean;
  attendanceDuration: number;
};

// NB: the definition of invited users is the union of explicitly invited users
// and all users that are part of the invited worker team, audience, etc. between
// when the webinar is created and when the webinar starts. Because we don't
// have a good way of tracking this set over time, we approximate it by taking the union of
// the explicitly invited users, the registered users, and the users that are
// part of the worker team, audience, etc. at the time of query.
const getInvitedUsers = (
  session: CollectionSessionWithHydratedUsers,
): {
  users: HydratedUser[];
  truncated: boolean;
  totalUsers: number;
} => {
  const allInvited = new Set([...session.rsvpUsers, ...session.allInvitedUsersInfo.users]);
  return {
    users: Array.from(allInvited),
    truncated: session.allInvitedUsersInfo.truncated,
    totalUsers: session.allInvitedUsersInfo.totalCount,
  };
};

type AggregateUsers = {
  aggregateUsers: AggregateUser[];
  userStats: {
    invitedUsersCount: number;
    invitedUsersTruncated: boolean;
  };
};

const aggregateUsersByStatus = (session: CollectionSessionWithHydratedUsers): AggregateUsers => {
  const registered = session.rsvpUsers;
  const attended = session.attendees;

  const {
    users: allInvited,
    truncated: allInvitedTruncated,
    totalUsers: allInvitedCount,
  } = getInvitedUsers(session);
  const aggregateUsers: Map<string, AggregateUser> = new Map();

  for (const user of [...allInvited, ...registered, ...attended]) {
    aggregateUsers.set(user._id, {
      id: user._id,
      email: user.email ?? '',
      invited: false,
      registered: false,
      attended: false,
      attendanceDuration: 0,
    });
  }

  for (const user of attended) {
    aggregateUsers.get(user._id)!.attended = true;
  }

  for (const user of allInvited) {
    aggregateUsers.get(user._id)!.invited = true;
  }

  for (const user of registered) {
    aggregateUsers.get(user._id)!.registered = true;
  }

  const aggregateUserByEmail = new Map(
    Array.from(aggregateUsers.values()).map(user => [
      user.email?.toLocaleLowerCase().replace('+outlier', '').replace('+worker@', '@'),
      user,
    ]),
  );

  const unaccountedUserByEmail = new Map<string, AggregateUser>();

  const normalizeEmail = (email: string) =>
    email.toLocaleLowerCase().replace('+outlier', '').replace('+worker@', '@');

  (session.zoomRegistrants ?? []).forEach(({ email }) => {
    const normEmail = normalizeEmail(email);

    if (aggregateUserByEmail.has(normEmail) || unaccountedUserByEmail.has(normEmail)) return;

    unaccountedUserByEmail.set(normEmail, {
      id: 'N/A',
      invited: false,
      registered: true,
      attended: false,
      email: normEmail,
      attendanceDuration: 0,
    });
  });

  (session.zoomAttendees ?? []).forEach(({ email, status, duration }) => {
    if (status !== 'in_meeting') return;

    const normEmail = normalizeEmail(email);
    const user = aggregateUserByEmail.get(normEmail);

    if (user) {
      user.attendanceDuration += duration;
    } else {
      const existing = unaccountedUserByEmail.get(normEmail);
      unaccountedUserByEmail.set(normEmail, {
        id: 'N/A',
        invited: existing?.invited ?? false,
        registered: existing?.registered ?? false,
        attended: true,
        email: normEmail,
        attendanceDuration: (existing?.attendanceDuration ?? 0) + duration,
      });
    }
  });

  return {
    aggregateUsers: [
      ...Array.from(aggregateUserByEmail.values()),
      ...Array.from(unaccountedUserByEmail.values()),
    ].sort((a, b) => {
      if (a.id === 'N/A' && b.id !== 'N/A') return 1;
      if (a.id !== 'N/A' && b.id === 'N/A') return -1;

      return a.email?.localeCompare(b.email) ?? 0;
    }),
    userStats: {
      invitedUsersCount: allInvitedCount,
      invitedUsersTruncated: allInvitedTruncated,
    },
  };
};

const getFilteredUsers = (allUsers: AggregateUser[], filters: Filters) => {
  const search = filters.search.toLocaleLowerCase();
  const matchedUsers = allUsers.filter(
    user => user.email?.toLocaleLowerCase().includes(search) || user.id.includes(search),
  );

  if (filters.category === FilterCategory.Invited) {
    return matchedUsers.filter(user => user.invited);
  } else if (filters.category === FilterCategory.Registered) {
    return matchedUsers.filter(user => user.registered);
  } else if (filters.category === FilterCategory.Attended) {
    return matchedUsers.filter(user => user.attended);
  } else if (filters.category === FilterCategory.NoResponse) {
    return matchedUsers.filter(user => user.invited && !user.registered && !user.attended);
  }

  return matchedUsers;
};

const InviteeList = ({
  session,
  filteredUsers,
  elapsed,
  filters,
  setFilters,
}: {
  session: CollectionSessionWithHydratedUsers;
  filteredUsers: AggregateUser[];
  elapsed: boolean;
  filters: Filters;
  setFilters: (filters: Filters) => void;
}) => {
  const columnHelper = DataTable.createColumnHelper<AggregateUser>();

  const columns = [
    columnHelper.accessor('email', {
      id: 'email',
      header: () => <DataTable.CellContentDefault>Email</DataTable.CellContentDefault>,
      cell: props => (
        <DataTable.CellContentDefault>
          <Text>{props.getValue()}</Text>
        </DataTable.CellContentDefault>
      ),
    }),
    columnHelper.accessor('id', {
      id: 'id',
      header: () => <DataTable.CellContentDefault>ID</DataTable.CellContentDefault>,
      cell: props => (
        <DataTable.CellContentDefault>
          <Text size="1" color="neutral">
            {props.getValue()}
          </Text>
        </DataTable.CellContentDefault>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: () => <DataTable.CellContentDefault>Attendance Status</DataTable.CellContentDefault>,
      cell: props => {
        const { attended, registered, invited } = props.row.original;

        let badgeText = 'unknown';
        let badgeColor: 'neutral' | 'success' | 'warning' | 'info' = 'neutral';

        if (attended) {
          badgeText = 'Attended';
          badgeColor = 'success';
        } else if (registered) {
          badgeText = 'Registered';
          badgeColor = 'info';
        } else if (invited) {
          badgeText = 'No Response';
          badgeColor = 'neutral';
        } else {
          badgeText = 'Not Invited';
          badgeColor = 'neutral';
        }

        const noUserFoundBadge =
          props.row.original.id === 'N/A' ? (
            <Badge color="warning" size="1" style={{ alignSelf: 'flex-start' }}>
              No User Found
            </Badge>
          ) : undefined;

        const duration = props.row.original.attendanceDuration;

        return (
          <Flex justify="between" align="center" gap="2">
            <Flex align="center" gap="2">
              <Badge color={badgeColor} size="1" style={{ alignSelf: 'flex-start' }}>
                {badgeText.charAt(0).toUpperCase() + badgeText.slice(1)}
              </Badge>
              {noUserFoundBadge}
            </Flex>
            {elapsed && duration > 0 && (
              <Text size="1" color="neutral">
                {formatDurationSeconds(duration)}
              </Text>
            )}
          </Flex>
        );
      },
    }),
  ];

  return (
    <DataTable.Root columns={columns} data={filteredUsers}>
      {({ table }) => (
        <>
          <InviteeControls
            session={session}
            filteredUsers={filteredUsers}
            filters={filters}
            onFiltersChange={setFilters}
          />
          <DataTable.Table>
            <DataTable.Header>
              {table.getHeaderGroups().map(headerGroup => (
                <DataTable.HeaderGroup key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <DataTable.ColumnHeaderCell key={header.id} header={header} />
                  ))}
                </DataTable.HeaderGroup>
              ))}
            </DataTable.Header>
            <DataTable.Body>
              {table.getRowModel().rows.map(row => (
                <DataTable.Row key={row.id} row={row}>
                  {row.getVisibleCells().map(cell => (
                    <DataTable.Cell key={cell.id} cell={cell} />
                  ))}
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable.Table>
          <DataTable.Pagination>
            <DataTable.PaginationPageNavigation />
            <DataTable.PaginationPageSize />
            <DataTable.PaginationTotal />
          </DataTable.Pagination>
        </>
      )}
    </DataTable.Root>
  );
};

const InviteeControls = ({
  session,
  filteredUsers,
  filters,
  onFiltersChange,
}: {
  session: GetCollectionSession;
  filteredUsers: AggregateUser[];
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}) => {
  const now = moment();
  const estimatedEventEnd = moment(session.eventStart).add(session.duration, 'minutes');
  const isEventEnded = now.isAfter(estimatedEventEnd);

  return (
    <DataTable.Actions justify="between" className="mt-4">
      <Box
        display={{
          initial: 'none',
          md: 'block',
        }}
      >
        <SegmentedControl.Root
          defaultValue={FilterCategory.All}
          size="1"
          variant="surface"
          onValueChange={value =>
            onFiltersChange({ ...filters, category: value as FilterCategory })
          }
        >
          <SegmentedControl.Item value={FilterCategory.All}>All</SegmentedControl.Item>
          <SegmentedControl.Item value={FilterCategory.Invited}>Invited</SegmentedControl.Item>
          <SegmentedControl.Item value={FilterCategory.Registered}>
            Registered
          </SegmentedControl.Item>
          {isEventEnded && (
            <SegmentedControl.Item value={FilterCategory.Attended}>Attended</SegmentedControl.Item>
          )}
          <SegmentedControl.Item value={FilterCategory.NoResponse}>
            No Response
          </SegmentedControl.Item>
        </SegmentedControl.Root>
      </Box>
      <Flex align="center" gap="2">
        <DataTable.ActionsSearch
          placeholder="Search by email or ID"
          value={filters.search}
          onChange={event => onFiltersChange({ ...filters, search: event.target.value })}
        />
        <Tooltip content="Download CSV">
          <IconButton variant="outline" color="neutral" size="1">
            <CSVLink
              data={filteredUsers}
              filename={`${session.name}_${filters.category}_invitees.csv`}
            >
              <Icon icon={faDownload} size="2" label="Download" />
            </CSVLink>
          </IconButton>
        </Tooltip>
      </Flex>
    </DataTable.Actions>
  );
};

interface Props {
  session: CollectionSessionWithHydratedUsers | undefined;
  collectionId: string;
  isLoading: boolean;
  refetch: () => void;
}

const WebinarDetailInvitees = ({ session, collectionId, isLoading, refetch }: Props) => {
  const queryClient = useQueryClient();
  const { mutate: onSync, isLoading: isSyncing } = useMutation({
    mutationFn: () => {
      if (!session) throw new Error('Session is undefined');
      return collectRegistrants(session._id);
    },
    onSuccess: () => {
      toast.success('Registrants collected successfully!');
      queryClient.invalidateQueries({
        queryKey: ['useGetSessionsForCollection', collectionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['useGetSessionsForCollectionWithHydratedUsers', collectionId],
      });
    },
  });

  const [filters, setFilters] = useState<Filters>({
    category: FilterCategory.All,
    search: '',
  });

  const allUsers = useMemo(
    () => (session ? aggregateUsersByStatus(session) : undefined),
    [session],
  );
  const filteredUsers = useMemo(
    () => (allUsers ? getFilteredUsers(allUsers.aggregateUsers, filters) : []),
    [allUsers, filters],
  );

  const elapsed = session ? isPastSession(session) : false;

  return (
    <Skeleton loading={isLoading}>
      <Card variant="surface" className="p-5 flex flex-col gap-4 flex-1 overflow-visible">
        <Flex align="center" justify="between">
          <Heading size="4">Invitees</Heading>
          {elapsed && session && <AddAttendees session={session} refetch={refetch} />}
        </Flex>
        {allUsers && <MetricCardList allUsers={allUsers} onSync={onSync} isSyncing={isSyncing} />}
        {session && (
          <InviteeList
            session={session}
            filteredUsers={filteredUsers}
            elapsed={elapsed}
            filters={filters}
            setFilters={setFilters}
          />
        )}
      </Card>
    </Skeleton>
  );
};

export default WebinarDetailInvitees;
